import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useLazyQuery, gql } from "@apollo/client";
import useAuth from "../hooks/useAuth";

// GraphQL query to check permission status
const CHECK_PERMISSION_STATUS = gql`
  query CheckPermissionStatus($userId: ID!) {
    checkPermissionStatus(userId: $userId) {
      id
      permissionsUpdatedAt
    }
  }
`;

/**
 * PermissionWatcher component
 * Checks for permission updates on:
 * 1. Route navigation
 * 2. Tab visibility change (when user returns to the tab)
 * 3. Window focus (when user clicks back into the app)
 * When permissions are updated (role change, etc.), forces logout.
 */
const PermissionWatcher = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const lastCheckedPath = useRef(null);

  // Only check if we have a logged-in team member (user under a client)
  // Team members have user.client (their parent client), clients don't have this property
  const isTeamMember = isAuthenticated && user?.id && user?.client;

  const handlePermissionUpdate = useCallback(() => {
    console.log("Permissions have been updated, logging out...");

    // Show notification to user
    alert(
      "Your permissions have been updated. Please log in again to continue."
    );

    // Clear all auth data
    localStorage.removeItem("accessToken");
    localStorage.removeItem("permissionsUpdatedAt");
    localStorage.removeItem("rbacPermissions");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminPermissions");
    localStorage.removeItem("impersonatedUser");

    // Redirect to login
    window.location.href = "/auth/sign-in";
  }, []);

  const [checkPermission] = useLazyQuery(CHECK_PERMISSION_STATUS, {
    fetchPolicy: "network-only", // Always fetch fresh from server
    onCompleted: (data) => {
      const serverTimestamp = data?.checkPermissionStatus?.permissionsUpdatedAt;
      const storedTimestamp = localStorage.getItem("permissionsUpdatedAt");

      if (serverTimestamp && storedTimestamp) {
        const serverDate = new Date(serverTimestamp);
        const storedDate = new Date(storedTimestamp);

        if (serverDate > storedDate) {
          console.log("Permission change detected:", {
            server: serverTimestamp,
            stored: storedTimestamp,
          });
          handlePermissionUpdate();
        }
      }
    },
    onError: (error) => {
      console.error("Permission check error:", error);
    },
  });

  // Memoized check function
  const doCheck = useCallback(() => {
    if (isTeamMember && user?.id) {
      checkPermission({ variables: { userId: user.id } });
    }
  }, [isTeamMember, user?.id, checkPermission]);

  // Check permissions on route change
  useEffect(() => {
    if (isTeamMember && location.pathname !== lastCheckedPath.current) {
      lastCheckedPath.current = location.pathname;
      doCheck();
    }
  }, [location.pathname, isTeamMember, doCheck]);

  // Check when user returns to the tab or window
  useEffect(() => {
    if (!isTeamMember) return;

    // Check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        doCheck();
      }
    };

    // Check when window gains focus
    const handleFocus = () => {
      doCheck();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Initial check on mount
    doCheck();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isTeamMember, doCheck]);

  // This component doesn't render anything
  return null;
};

export default PermissionWatcher;
